// ── VEXOS CLIENT CALCULATOR · PAYMENT PLAN RESOLVER ─────────────────────
//
// The single seam every surface reads for "how does this quote get paid".
// Mirrored in backend/services/vexos_payment_plan.py — change both together
// or the calculator, the web proposal, the PDF, and the Stripe deposit will
// stop agreeing with each other.
//
// Three plans:
//
//   50-25-25  The default, for every quote whose one-time scope is $100,000 or
//             under. The VEXOS setup fee joins the split rather than riding
//             Payment 1 alone: setup + viz together split 50 / 25 / 25, so half
//             the engagement is collected at signing.
//   25-50-25  Large quotes only — one-time scope OVER $100,000. Half the money
//             moves to the V1 milestone so the opening payment on a big
//             engagement stays reasonable, and the setup fee goes back to
//             billing entirely at signing. Threshold set by Bernard 2026-08-06.
//   50-50     RETIRED for new quotes (Cole, 2026-11-02). À La Carte quotes
//             saved with it still resolve to it, so the terms a client was
//             sent never move; the calculator no longer offers it.
//   75-25     75% at signing, the remaining 25% on finalization. No V1
//             payment in between. The setup fee joins the split. Bernard,
//             2026-09-29 (Hope Town Club). THE DEFAULT for every quote created
//             from 2026-11-02 — see NEW_QUOTE_PAY_PLAN.
//   100       One payment at signing, chosen by the rep (see PLAN_FULL).
//
// The order reps sell in (Cole, 2026-11-02): pay in full first, then 75 / 25,
// and the three-payment plan only when a client pushes back hard. The
// three-payment plan is "auto" — 50 / 25 / 25, or 25 / 50 / 25 over $100k.
//
// The two-payment plans (50-50 and 75-25) share one SHAPE: Payment 2 IS the
// finalization payment and Payment 3 is empty. Surfaces test `twoPart`, never
// the plan key, so both render as "Due at signing" + "Due on finalization".
//
// On-site travel is a flat pass-through cost and always bills in full at
// signing, on top of whichever plan applies. Rounding lands on Payment 1 so
// the milestones always sum EXACTLY to the one-time scope.
//
// COLLATERAL rides the split alongside the visualization scope: viz +
// collateral are ONE base divided by the plan percentages. It used to split
// 50 / 50 across Payments 1 and 2, which left the finalization payment holding
// nothing but a quarter of the visualization — $900 of a $12,100 engagement on
// the quote that surfaced this (Bernard, 2026-08-24). The marketing campaign
// setup fee deliberately stays OUT of the split and bills whole at signing:
// that work is done up front.
//
// A quote with no visualization and no platform — marketing & collateral only —
// has no three-milestone plan to ride, so its collateral keeps the standalone
// 50 / 50 schedule (signing / delivery of collateral) that surface renders
// itself. `collateralInSplit` says which of the two applies.
//
// Plan constants are named for their percentages, not for which one is
// "standard" — that has already flipped once.

// A quote must EXCEED this one-time total to earn 25 / 50 / 25. Exactly
// $100,000 is not over it, so it stays on 50 / 25 / 25.
export const LARGE_QUOTE_THRESHOLD = 100000;

export const PLAN_50_25_25 = "50-25-25";
export const PLAN_25_50_25 = "25-50-25";
export const PLAN_TWO_PART = "50-50";
/** One payment, the whole one-time scope, due at signing. Chosen by the rep on
 *  any quote — it is the terms, not an option printed under them. */
export const PLAN_FULL = "100";
/** 75% at signing, 25% on finalization (before final delivery). Chosen by the
 *  rep on any quote. Its second payment is the FINAL one, so it is a two-part
 *  plan: Payment 2 carries the 25%, Payment 3 is empty. */
export const PLAN_75_25 = "75-25";

/** The plan a brand-new quote opens on. Only the calculator's initial state
 *  reads it: a quote saved before 2026-11-02 has no `payPlanChoice`, so it
 *  hydrates to auto and keeps the three-payment terms it was sent. */
export const NEW_QUOTE_PAY_PLAN = PLAN_75_25;

const PLAN_PCT = {
  [PLAN_50_25_25]: [50, 25, 25],
  [PLAN_25_50_25]: [25, 50, 25],
  [PLAN_TWO_PART]: [50, 50, 0],
  [PLAN_75_25]: [75, 25, 0],
  [PLAN_FULL]: [100, 0, 0],
};

/** Plans where Payment 2 is the finalization payment and there is no third. */
const TWO_PART_PLANS = [PLAN_TWO_PART, PLAN_75_25];
/** Plans the setup fee joins the split on, rather than billing whole at
 *  signing. Only the large-quote 25 / 50 / 25 keeps it out. */
const SETUP_IN_SPLIT_PLANS = [PLAN_50_25_25, PLAN_75_25, PLAN_FULL];

/** The rep's explicit plan choice, persisted on `inputs.payPlanChoice`. Empty
 *  means "let the rules decide" — the à-la-carte choice, then the size rule.
 *  Deliberately NOT stored on `inputs.alacartePayPlan`: 100% applies to VEXOS
 *  quotes too, and filing a VEXOS election under an à-la-carte key is how a
 *  field ends up meaning two things. */
export const PAY_PLAN_AUTO = "";

/** The values `inputs.payPlanChoice` may hold besides "auto". */
const REP_PLAN_CHOICES = [PLAN_FULL, PLAN_75_25];

/** A stored `payPlanChoice`, cleaned: a known rep choice, else auto. Used on
 *  save and on hydrate so an unknown value can never pick a plan. */
export function normalizePayPlanChoice(value) {
  return REP_PLAN_CHOICES.includes(value) ? value : PAY_PLAN_AUTO;
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** The à la carte payment-plan choice as it is persisted on
 *  `inputs.alacartePayPlan`: this value means "three payments, let the size
 *  rule pick the split", NOT literally 25 / 50 / 25. Legacy quotes carry it. */
export const ALACARTE_THREE_PART = "25-50-25";

/** The scope the plan divides BEFORE collateral — platform setup fee (VEXOS
 *  only) + visualization scope + revisions. Collateral only joins the split
 *  when this is non-zero, so it is the plan-independent test for "is there a
 *  three-milestone schedule here at all". */
function coreScopeOf({ setupFee = 0, vizSubtotal = 0 }) {
  return num(setupFee) + num(vizSubtotal);
}

/** Whether the collateral one-time rides the plan percentages (the normal
 *  case) or keeps its standalone 50 / 50 signing / delivery schedule (a
 *  marketing & collateral quote, which has no visualization milestones to hang
 *  it on). Surfaces must check this before folding collateral in themselves. */
export function collateralInSplitOf({ setupFee = 0, vizSubtotal = 0, collateralTotal = 0 }) {
  return num(collateralTotal) > 0 && coreScopeOf({ setupFee, vizSubtotal }) > 0;
}

/** The one-time total the payment schedule governs: platform setup fee (VEXOS
 *  only) + visualization scope + revisions + collateral (when it rides the
 *  split) + on-site travel. This — not the grand total with the monthly
 *  subscription — is what the size rule tests, because it is the money the
 *  three milestones actually divide. The marketing campaign setup fee is
 *  outside it for the same reason: the milestones never divide it. */
export function oneTimeScopeOf({ setupFee = 0, vizSubtotal = 0, travelCost = 0, collateralTotal = 0 }) {
  const collateral = collateralInSplitOf({ setupFee, vizSubtotal, collateralTotal }) ? num(collateralTotal) : 0;
  return coreScopeOf({ setupFee, vizSubtotal }) + num(travelCost) + collateral;
}

export function isLargeQuote(oneTimeScope) {
  return num(oneTimeScope) > LARGE_QUOTE_THRESHOLD;
}

/** Which plan a quote uses. The rep's explicit choices win over the size rule,
 *  in order: a single payment first, then the à-la-carte 50/50. Both collect
 *  MORE up front than any three-payment plan, and the size rule exists to stop
 *  the opening payment on a big engagement being unreasonable — so there is
 *  nothing for it to protect against. */
export function resolvePlanKey({
  oneTimeScope,
  isAlacarte = false,
  alacartePayPlan = ALACARTE_THREE_PART,
  payPlanChoice = PAY_PLAN_AUTO,
}) {
  if (payPlanChoice === PLAN_FULL) return PLAN_FULL;
  if (payPlanChoice === PLAN_75_25) return PLAN_75_25;
  if (isAlacarte && alacartePayPlan === PLAN_TWO_PART) return PLAN_TWO_PART;
  if (isLargeQuote(oneTimeScope)) return PLAN_25_50_25;
  return PLAN_50_25_25;
}

/** Whether a plan collects everything in one payment at signing. The one
 *  predicate every surface should test rather than comparing plan keys, because
 *  what it changes is the SHAPE of the schedule: one row, no delivery gates. */
export function isSinglePayment(planKey) {
  return planKey === PLAN_FULL;
}

/** Whether a plan collects in two payments — signing, then finalization. */
export function isTwoPart(planKey) {
  return TWO_PART_PLANS.includes(planKey);
}

/** Milestone amounts for a plan. Travel always lands on Payment 1; the setup
 *  fee joins the split on 50 / 25 / 25 and bills whole at signing on the
 *  large-quote 25 / 50 / 25; collateral joins the split whenever there is one
 *  (see collateralInSplitOf). */
export function splitPayments({ setupFee = 0, vizSubtotal = 0, travelCost = 0, collateralTotal = 0, planKey = PLAN_50_25_25 }) {
  const pct = PLAN_PCT[planKey] || PLAN_PCT[PLAN_50_25_25];
  const setup = num(setupFee);
  const viz = num(vizSubtotal);
  const travel = num(travelCost);
  const collateral = collateralInSplitOf({ setupFee, vizSubtotal, collateralTotal }) ? num(collateralTotal) : 0;
  // On a single payment everything lands on Payment 1 regardless, so this only
  // decides the COPY — "100% of setup + viz scope" rather than "VEXOS Setup +
  // 100% of viz scope", which would read as two separate charges.
  const setupInSplit = SETUP_IN_SPLIT_PLANS.includes(planKey);
  const base = (setupInSplit ? setup : 0) + viz + collateral;
  const atSigning = setupInSplit ? 0 : setup; // setup fee billed whole at signing
  const p2 = Math.round((base * pct[1]) / 100);
  const p3 = Math.round((base * pct[2]) / 100);
  const p1 = base - p2 - p3 + atSigning + travel; // absorbs rounding
  return { p1, p2, p3 };
}

// ── PAY IN FULL (PIF) ───────────────────────────────────────────────────
//
// Every proposal offers the client one alternative to the milestone schedule:
// settle the whole one-time scope in a single payment at signing and take
// PIF_DISCOUNT_PCT off it. It replaces the milestones — it is not an extra
// payment alongside them.
//
// What it does NOT touch:
//   • On-site travel. It is a flat pass-through cost billed at cost, so it
//     rides the pay-in-full total at face value and never earns the discount.
//   • Recurring money — the platform subscription, marketing management, the
//     social retainer. "In full" is about the one-time scope; a monthly term
//     keeps running on its own schedule.
//
// The percentage is stated against a printed one-time figure, so a client can
// check the arithmetic on the page. That is why this survives `manualPricing`
// (a hand-priced quote), where rate-card savings copy is suppressed: the
// saving here IS exactly PIF_DISCOUNT_PCT of the total printed directly above it.
//
// THE DISCOUNT IS OPTIONAL. The rep can switch it off per quote
// (`inputs.pifDiscount === false`), which leaves the single-payment option on
// the proposal at its FULL price: same "one payment in place of the milestones"
// offer, 0% off, no savings line anywhere. It is a discount toggle, not a
// pay-in-full toggle — turning it off must never remove the option itself.
// Default is ON, so every quote saved before the toggle existed keeps the discount.
//
// Mirrored in services/vexos_payment_plan.py — change both together.

export const PIF_DISCOUNT_PCT = 5;

/** The discount percentage a quote's pay-in-full option carries: the standard
 *  PIF_DISCOUNT_PCT, or 0 when the rep switched the discount off. Anything other than an
 *  explicit `false` means ON, so a quote with no stored flag (every quote that
 *  pre-dates the toggle) keeps the discount.
 *  Mirrors `pif_pct_of()` in services/vexos_payment_plan.py. */
export function pifPctOf(pifDiscount) {
  return pifDiscount === false ? 0 : PIF_DISCOUNT_PCT;
}

/** The pay-in-full offer for a given pot of one-time money.
 *  `discountable` is the one-time scope that earns the discount; `atCost` is
 *  pass-through money (on-site travel) that is billed at face value.
 *  Rounding lands on the saving so the total is always exact.
 *  Mirrors `pif_offer()` in services/vexos_payment_plan.py. */
export function pifOffer({ discountable = 0, atCost = 0, pct = PIF_DISCOUNT_PCT } = {}) {
  const base = Math.max(0, num(discountable));
  const cost = Math.max(0, num(atCost));
  const rate = Math.max(0, num(pct));
  const savings = Math.round((base * rate) / 100);
  return {
    pct: rate,
    discountable: base,
    atCost: cost,
    savings,
    total: base - savings + cost,
    // Nothing to offer on a quote with no one-time money (a subscription-only
    // or ad-spend-only engagement): a "$0, save $0" row is noise. With the
    // discount switched off there is no saving to test, so eligibility falls
    // back to "is there one-time money here at all" — the option still stands,
    // it just costs full price.
    eligible: rate > 0 ? savings > 0 : base > 0,
  };
}

/** What a client pays to settle an ENTIRE quote at signing.
 *
 *  `oneTimeTotal` is every one-time dollar the proposal bills — the plan's
 *  three milestones plus any marketing campaign setup and standalone
 *  collateral the surface adds on top. `travelCost` is the pass-through slice
 *  inside it that is billed at cost and earns no discount.
 *
 *  One rule so the figure the calculator shows, the figure the proposal
 *  prints, and the amount Stripe charges are the same number. Clamped so a
 *  stale travel figure larger than the bill can never produce a negative
 *  discountable base.
 *
 *  `pct` is the quote's own rate and must be passed in — this rebuilds the
 *  offer over a WIDER pot than the plan divides, and leaving it at the default
 *  would silently restore a discount the rep switched off.
 *  Mirrors `pay_in_full_total()` in services/vexos_payment_plan.py. */
export function payInFullTotal({ oneTimeTotal = 0, travelCost = 0, pct = PIF_DISCOUNT_PCT } = {}) {
  const total = Math.max(0, num(oneTimeTotal));
  const travel = Math.max(0, Math.min(num(travelCost), total));
  return pifOffer({ discountable: total - travel, atCost: travel, pct }).total;
}

/** Client-facing copy for the pay-in-full row. Kept here so the calculator,
 *  the web proposal, and the PDF can never word the same offer differently.
 *  Mirrors `pif_copy()` in services/vexos_payment_plan.py. */
export function pifCopy({ atCost = 0, hasRecurring = false, pct = PIF_DISCOUNT_PCT } = {}) {
  const rate = Math.max(0, num(pct));
  return {
    // No discount, no saving to advertise — the label states what the option
    // IS. It must not imply a price break that the amount beside it doesn't
    // give.
    label: rate > 0 ? `Pay in full at signing — save ${rate}%` : "Pay in full at signing — a single payment",
    sub: [
      "A single payment covering the entire one-time scope, in place of the milestones above.",
      // Only worth saying while there is a discount for travel to sit outside.
      rate > 0 && num(atCost) > 0 ? "On-site travel is billed at cost and is not discounted." : null,
      hasRecurring ? "Recurring fees are unaffected and continue on their regular term." : null,
    ].filter(Boolean).join(" "),
  };
}

// ── PAYMENT 2 MILESTONE ("V1 of what?") ────────────────────────────────
//
// Payment 2 is gated on ONE delivery: the V1 Clay package.
//
// V1 Clay reaches the client in ReviewStudio as a single package holding the
// clay orbit and its sub-orbits, the corresponding keyframes, clay renderings
// for every still in scope, and 360 tours in clay wherever the scope includes
// them. Materials, finishes and final color are applied in the FOLLOWING stage
// and are not a condition of this milestone.
//
// Deliberately outside it:
//
//   * materials, finishes and color renders — the following stage;
//   * the VEXOS platform build (project data, unit inventory, unit browser,
//     CRM, domain) — a separate production on its own rail;
//   * animation and video, which are produced from the approved renderings.
//
// THE WORD "PLATFORM" IS BANNED FROM THIS MILESTONE'S COPY. It used to be
// called "V1 Platform", which clients read as "the whole VEXOS build is live"
// — the expectation gap behind the Station House problem (Bernard,
// 2026-09-17). At this milestone "platform" only ever meant the orbital
// component, so the milestone is now named for what it actually contains:
// clay. Anything that reintroduces the word here reintroduces the gap.
//
// A quote with no clay stage at all (floor plans only) gates on that delivery
// instead.

// ── PAYMENT 3 (THE FINAL PAYMENT) ───────────────────────────────────────
//
// Deliberately generic. Payment 3 is the finalization payment: whatever the
// remaining visualization assets are for this quote. Naming specific asset
// types here would only ever describe one kind of quote.
//
// TWO sets of terms, stamped on the quote at create time as
// `final_payment_policy` (same mechanism as `milestone_policy`):
//
//   legacy    Collected on finalization, once the client has reviewed and
//             APPROVED the work, before final delivery. A client who sat on
//             approval — or waited for their launch — sat on the payment.
//   notice21  Due when the finalized assets are ready for delivery, on a
//             date the client gets at least three weeks' notice of. The PM
//             approves that notice before it goes out — HQ asks four weeks
//             ahead (services/final_payment_notice; Bernard, 2026-11-02:
//             "things change all the time"). Approval and launch are not a
//             condition. Past the due date the existing late-payment terms
//             run. Every quote created from 2026-11-02 carries it.
//
// `buffer21` (due three weeks after the finals are PRESENTED) lived for one
// day and was the wrong rule; quotes stamped with it resolve to notice21.
export const FINAL_ASSETS = "remaining visualization assets";
export const FINAL_PAYMENT_NOTE =
  "Covers the remaining visualization assets. Collected on finalization — once the work is complete and you have reviewed and approved it — and prior to final delivery of the approved assets.";

export const FINAL_POLICY_LEGACY = "legacy";
export const FINAL_POLICY_NOTICE = "notice21";
const FINAL_POLICY_NOTICE_ALIASES = new Set([FINAL_POLICY_NOTICE, "buffer21"]);
/** The client's minimum notice of the final payment's due date. Mirrors
 *  FINAL_PAYMENT_NOTICE_DAYS in services/vexos_payment_plan.py. */
export const FINAL_PAYMENT_NOTICE_DAYS = 21;

/** The final-payment terms this quote was sold under. Legacy unless the quote
 *  says otherwise — a missing field must never move an old quote's terms.
 *  Mirrors `quote_final_payment_policy()` in services/vexos_payment_plan.py. */
export function quoteFinalPaymentPolicy(quote) {
  return FINAL_POLICY_NOTICE_ALIASES.has((quote || {}).final_payment_policy)
    ? FINAL_POLICY_NOTICE
    : FINAL_POLICY_LEGACY;
}

/** What the final payment covers, in the client's words. Collateral rides the
 *  split, so when a quote carries it the finalization payment collects a share
 *  of the collateral too and the copy has to say so — otherwise the note names
 *  visualization over an amount that is part collateral.
 *  Mirrors `final_copy()` in services/vexos_payment_plan.py. */
export function finalCopy({ hasViz = true, collateralInSplit = false, policy = FINAL_POLICY_LEGACY } = {}) {
  const assets = [hasViz && FINAL_ASSETS, collateralInSplit && "collateral"].filter(Boolean).join(" and ") || FINAL_ASSETS;
  if (policy === FINAL_POLICY_NOTICE) {
    return {
      assets,
      note: `Covers the ${assets}. Due when the finalized assets are ready for delivery — your project manager confirms the date in writing at least three weeks ahead, so your team has time to process it. It does not wait for final approval or for your launch, and final delivery follows payment. Past the due date the late-payment terms apply: 1.5% per month, a $5,000 reinstatement fee, and production pauses until the balance is settled.`,
    };
  }
  return {
    assets,
    note: `Covers the ${assets}. Collected on finalization — once the work is complete and you have reviewed and approved it — and prior to final delivery of the approved assets.`,
  };
}

/** The short "when" clause the calculator sidebar prints after the final
 *  payment's assets ("— remaining visualization assets, …"). */
export function finalTiming(policy = FINAL_POLICY_LEGACY) {
  return policy === FINAL_POLICY_NOTICE
    ? `, due when finals are ready, with ${FINAL_PAYMENT_NOTICE_DAYS / 7} weeks' notice confirmed by the PM`
    : ", complete and client-approved; collected prior to final delivery";
}

/** One sentence on when the final payment falls due, for the V1 note that
 *  sits under a three-payment schedule. Worded per the quote's final-payment
 *  terms — see finalCopy(). */
export function finalDueSentence({ pct, assets = FINAL_ASSETS, policy = FINAL_POLICY_LEGACY } = {}) {
  if (policy === FINAL_POLICY_NOTICE) {
    return `The final ${pct}% is due when the finalized ${assets} are ready for delivery. Your project manager confirms the date with you at least three weeks ahead, and the final delivery goes out once it is paid.`;
  }
  return `The final ${pct}% is due on finalization — once the ${assets} are complete and you have approved them, and before the final delivery goes out.`;
}

// Which milestone definition a quote was sold under. Stamped on the quote
// document at CREATE time by routers/vexos_quotes and never on update, so a
// quote keeps the terms it was written with no matter how often it is edited.
// Absent means the quote predates the rename — see the legacy block below.
export const MILESTONE_POLICY_CLAY = "clay";
export const MILESTONE_POLICY_LEGACY = "platform";

/** The milestone definition this quote was sold under. Legacy unless the quote
 *  says otherwise — a missing field must never opt an old quote in. A quote
 *  the rep has not saved yet has no document, so the calculator passes
 *  MILESTONE_POLICY_CLAY explicitly for a brand-new one.
 *  Mirrors `quote_milestone_policy()` in services/vexos_payment_plan.py. */
export function quoteMilestonePolicy(quote) {
  return (quote || {}).milestone_policy === MILESTONE_POLICY_CLAY
    ? MILESTONE_POLICY_CLAY
    : MILESTONE_POLICY_LEGACY;
}

// ── V1 CLAY ────────────────────────────────────────────────────────────
//
// Clay is geometry, camera and framing with no materials on it yet. WHAT the
// package holds depends on what the quote sold, so the copy is assembled from
// the scope rather than written out once per quote type — a fixed sentence
// that promised 360 tours to a client who bought none would be the same class
// of over-promise as the old "V1 Platform" wording.
export const CLAY_STAGE_NOTE =
  "Clay is geometry, camera and framing. Materials, finishes and final color are applied in the following stage and are not a condition of this milestone.";

// The VEXOS build is a SEPARATE production. Saying so is the whole point of
// the rename: at this milestone the only "platform" that exists is the orbit.
// The landing page rides with it — it is presented at final delivery, and
// (from 2026-09-14) no V1 copy may list it among what this milestone contains.
const PLATFORM_DEFERRED =
  "The VEXOS platform build — your project data, unit inventory, unit browser, CRM and domain — is a separate production counted in its own VEXOS milestones, so it is not part of this milestone, and neither is your landing page.";
// Video is built FROM the approved renderings, so it can only start once this
// milestone is finished and coloured.
const ANIMATION_DEFERRED =
  "Animation and video are produced from the approved renderings, so they are not part of this milestone either — they continue on the production timeline and are billed at final delivery.";

/** What the V1 Clay package holds on THIS quote, as one readable list.
 *  Mirrors `clay_contents()` in services/vexos_payment_plan.py. */
export function clayContents(has = {}, isVexos = false) {
  const parts = [];
  // Every VEXOS engagement carries the exterior orbit, so the orbit line is
  // there even when no orbit ADD-ONS (extra keyframes, day-to-night, extra
  // orbits) were sold on top of it.
  if (isVexos || has.orbit) parts.push("the clay orbit and its sub-orbits with the corresponding keyframes");
  else if (has.video) parts.push("the clay keyframes your animation is built from");
  if (has.stills) parts.push("clay renderings for every still in scope");
  if (has.tour) parts.push("your 360° tours in clay");
  if (!parts.length) return "your clay renderings";
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** The V1 Clay milestone, worded for the scope in front of it.
 *  Mirrors `clay_milestone()` in services/vexos_payment_plan.py. */
export function clayMilestone(has = {}, isVexos = false) {
  const bullets = [
    `V1 Clay is your first visualization milestone, delivered in ReviewStudio as a single package containing ${clayContents(has, isVexos)}.`,
    CLAY_STAGE_NOTE,
  ];
  if (isVexos) bullets.push(PLATFORM_DEFERRED);
  if (has.video) bullets.push(ANIMATION_DEFERRED);
  return {
    key: "clay",
    dueLabel: "V1 Clay Delivery",
    dueLabelLower: "V1 clay delivery",
    shortLabel: "V1 Clay",
    title: 'What "V1 Clay" means',
    bullets,
    note: bullets.join(" "),
  };
}

// Floor plans have no clay stage, so a floor-plans-only quote keeps its own V1.
const V1_FLOOR_PLANS = {
  key: "floorPlans",
  dueLabel: "Delivery of the V1 Floor Plans",
  dueLabelLower: "delivery of the V1 floor plans",
  shortLabel: "V1 Floor Plans",
  bullets: ["Triggered upon delivery of the first-round floor plans for your review."],
  note: "Triggered upon delivery of the first-round floor plans for client review.",
};

// ── LEGACY: THE "V1 PLATFORM" MILESTONE ────────────────────────────────
//
// Kept verbatim, and not for reuse. A quote already sent to a client states
// the milestone it was sold under, and every surface in HQ recomputes its copy
// from the scope at render time — so without this branch the rename would have
// silently rewritten the terms of every proposal already out there. Bernard,
// 2026-09-17: new quotes only, existing quotes untouched.
//
// A quote carries `milestone_policy: "clay"` when the Client Calculator
// created it after the rename. Anything without that field — every quote that
// predates it — resolves here. Nothing new should be added to this block; when
// the last project sold under these terms closes, the whole thing goes.
const PLATFORM_BULLET =
  "V1 Platform is your VEXOS live and populated: the exterior orbit of the building, your full unit inventory, and all client-provided content — existing collateral imagery, verbiage, and project data.";
// Why video, tours and the landing page sit outside Payment 2: for video and
// tours the renderings are the foundation they are built from, so they can only
// start once the V1 renderings are approved and finished. The landing page is
// deliberately held back and presented at final delivery — it is NOT part of
// V1 Platform, and no V1 copy may imply that it is.
const DEFERRED_BULLET =
  "Video and 360 tours are produced from the approved renderings, and your landing page is presented later — none of them are part of this milestone; they continue on the production timeline and are billed at final delivery.";


// Platform only: no still renderings in scope, so the orbit is the whole of it.
const V1_PLATFORM = {
  key: "platform",
  dueLabel: "V1 Platform Delivery",
  dueLabelLower: "V1 platform delivery",
  shortLabel: "V1 Platform",
  title: 'What "V1 Platform" means',
  bullets: [
    PLATFORM_BULLET,
    "The only visualization scope tied to this milestone is the exterior orbit, the overlay programming, and the unit inventory.",
    DEFERRED_BULLET,
  ],
  note:
    "V1 Platform is your VEXOS live and populated: the exterior orbit of the building, your full unit inventory, and all client-provided content. The only visualization scope tied to this milestone is the exterior orbit, the overlay programming, and the unit inventory. Video and 360 tours are not part of V1 Platform, and neither is your landing page — they continue on the production timeline and are presented and billed at final delivery.",
};

// Platform + stills: the compound trigger. Both deliveries gate the payment.
const V1_PLATFORM_STILLS = {
  key: "platformStills",
  dueLabel: "V1 Platform + V1 Still Renderings",
  dueLabelLower: "V1 platform + V1 still renderings",
  shortLabel: "V1 Platform + Stills",
  title: 'What "V1 Platform" and "V1 Still Renderings" mean',
  bullets: [
    PLATFORM_BULLET,
    "V1 Still Renderings is the first-round presentation of your still imagery — exterior, interior, and amenity renderings — delivered for your review.",
    "Both deliveries gate this payment: it comes due once the platform is live and the V1 still renderings are in your hands.",
    DEFERRED_BULLET,
  ],
  note:
    "This milestone covers two deliveries. V1 Platform is your VEXOS live and populated: the exterior orbit of the building, your full unit inventory, and all client-provided content. V1 Still Renderings is the first-round presentation of your exterior, interior, and amenity still imagery. Video and 360 tours are not part of this milestone, and neither is your landing page — they continue on the production timeline and are presented and billed at final delivery.",
};

const V1_BY_SCOPE = [
  {
    key: "renders",
    dueLabel: "Delivery of V1 Renders",
    dueLabelLower: "delivery of V1 renders",
    shortLabel: "V1 Renders",
    note: "Triggered upon presentation of first-round renderings for client review.",
  },
  {
    key: "video",
    dueLabel: "Video Keyframe Delivery (V1)",
    dueLabelLower: "video keyframe delivery (V1)",
    shortLabel: "V1 Keyframes",
    note: "Triggered upon delivery of the V1 video keyframes for client review — collected before the keyframes are animated.",
  },
  {
    key: "tour",
    dueLabel: "Delivery of the V1 360° Tour",
    dueLabelLower: "delivery of the V1 360° tour",
    shortLabel: "V1 360° Tour",
    note: "Triggered upon delivery of the first-round 360° tour for client review.",
  },
  {
    key: "floorPlans",
    dueLabel: "Delivery of the V1 Floor Plans",
    dueLabelLower: "delivery of the V1 floor plans",
    shortLabel: "V1 Floor Plans",
    note: "Triggered upon delivery of the first-round floor plans for client review.",
  },
];

const V1_FALLBACK = {
  key: "v1",
  dueLabel: "Delivery of V1",
  dueLabelLower: "delivery of V1",
  shortLabel: "V1 Delivery",
  bullets: ["Triggered upon presentation of the first-round deliverables for your review."],
  note: "Triggered upon presentation of first-round deliverables for client review.",
};

/** Which asset families a quote actually contains. Reads either a live calc
 *  object or a saved `calc_snapshot` — both carry the same field names. */
export function scopeFamilies(scope = {}) {
  const s = scope || {};
  const any = (...vals) => vals.some((v) => num(v) > 0);
  // Still renderings ONLY — exterior, interior, and amenity stills. The
  // exterior orbit and its keyframe add-ons are counted separately below,
  // because V1 Clay names them as their own line in the package.
  const stills = any(
    s.exteriorRendersQty, s.exteriorRendersCost,
    s.conceptualRendersQty, s.conceptualRendersCost,
    s.totalInteriorRenders, s.interiorRendersCost,
    s.totalAmenityRenders, s.amenityRendersCost,
  );
  return {
    stills,
    // Day-to-night, extra + animated keyframes, additional orbits.
    orbit: any(s.exteriorAddonsSubtotal),
    video: any(s.videoAnimationCost, s.aiMotionCost, s.cinematicCost),
    tour: !!s.exteriorVRActive || any(
      s.exteriorVRCost, s.totalInteriorVR, s.interiorVRCost,
      s.totalAmenityVR, s.amenityVRCost,
    ),
    floorPlans: any(s.floorPlansCost, s.total2DPlans, s.total3DPlans),
  };
}

/** The delivery Payment 2 is gated on, under the terms this quote was sold.
 *
 *  Under `clay` it is ONE delivery. It used to branch four ways (platform /
 *  platform + stills / renders / keyframes / tour), because the platform and
 *  the imagery were different deliveries landing at different times; they are
 *  one package now, so every quote with a clay stage resolves to the same
 *  milestone, worded for its scope.
 *
 *  Under `platform` — the default, and every quote that predates the rename —
 *  the old branching applies unchanged. A proposal already in a client's hands
 *  must keep saying what it said. */
export function resolveV1Milestone(
  scope = {},
  { isAlacarte = false, milestonePolicy = MILESTONE_POLICY_LEGACY } = {},
) {
  const has = scopeFamilies(scope);
  if (milestonePolicy !== MILESTONE_POLICY_CLAY) return resolveV1Legacy(has, isAlacarte);
  // A VEXOS engagement always carries the exterior orbit, so there is always a
  // clay package to gate on — even on a quote with no imagery sold.
  if (!isAlacarte) return clayMilestone(has, true);
  if (has.stills || has.orbit || has.video || has.tour) return clayMilestone(has, false);
  // Floor plans never go through clay.
  if (has.floorPlans) return V1_FLOOR_PLANS;
  return V1_FALLBACK;
}

/** The pre-2026-09-17 trigger, frozen. Do not extend. */
function resolveV1Legacy(has, isAlacarte) {
  if (!isAlacarte) return has.stills ? V1_PLATFORM_STILLS : V1_PLATFORM;
  // Visualization-only: the orbit had no platform to ride, so its keyframes
  // counted as renders for the purposes of the V1 trigger.
  const present = { ...has, renders: has.stills || has.orbit };
  return V1_BY_SCOPE.find((m) => present[m.key]) || V1_FALLBACK;
}


/** The scope a milestone collects, with the percentage stated against each
 *  component separately. "25% of VEXOS setup fee + net visualization scope" is
 *  one percentage over a compound noun, and a client can't tell from it what
 *  share of the *visualization* they are paying for. The same percentage
 *  applies to every component in the split, so say it against each of them.
 *  Visualization leads — it's the number clients look for.
 *
 *  A component with no money in it is omitted entirely: a collateral-only
 *  à-la-carte quote must not print "50% of net visualization scope" over an
 *  amount that contains none.
 *  Mirrors `scope_parts()` in services/vexos_payment_plan.py. */
export function scopeParts(pct, { setupInSplit = false, hasViz = true, collateralInSplit = false, hasModeling = false, remaining = false } = {}) {
  const names = [
    hasViz && "net visualization scope",
    // The 3D modeling fee rides the visualization split; named on its own so
    // the schedule matches the line the client sees above it.
    hasModeling && "3D modeling",
    setupInSplit && "the VEXOS setup fee",
    collateralInSplit && "collateral",
  ].filter(Boolean);
  // "Remaining" capitalises on the first component only — it opens the line.
  return names.map((name, i) => `${remaining ? (i === 0 ? "Remaining " : "remaining ") : ""}${pct}% of ${name}`);
}

/** The compound noun the plan percentages apply to, in three registers:
 *  client view, internal sidebar, formal proposal. Built from the same
 *  component list as scopeParts() so a label can never name a component the
 *  amounts don't actually contain. */
function scopeLabels({ setupInSplit, hasViz, collateralInSplit, hasModeling = false }) {
  const pick = (setup, viz, modeling, col) => [
    setupInSplit && setup,
    hasViz && viz,
    hasModeling && modeling,
    collateralInSplit && col,
  ].filter(Boolean).join(" + ");
  return {
    scopeLabel: pick("VEXOS setup", "visualization scope", "3D modeling", "collateral") || "visualization scope",
    scopeLabelShort: pick("setup", "viz scope", "modeling", "collateral") || "viz scope",
    scopeLabelFormal: pick("VEXOS setup fee", "net visualization scope", "3D modeling", "collateral") || "net visualization scope",
  };
}

/** Everything a surface needs to render a payment schedule, from one call.
 *  `scope` is the live calc or the saved calc_snapshot (for the V1 trigger). */
export function resolvePaymentPlan({
  setupFee = 0,
  vizSubtotal = 0,
  travelCost = 0,
  collateralTotal = 0,
  isAlacarte = false,
  alacartePayPlan = ALACARTE_THREE_PART,
  payPlanChoice = PAY_PLAN_AUTO,
  scope = null,
  pifDiscount = true,
  // The milestone definition this quote was sold under. Legacy by default so a
  // caller that forgets it can never re-word an existing client's terms.
  milestonePolicy = MILESTONE_POLICY_LEGACY,
  // The 3D modeling fee, already INSIDE vizSubtotal. Passed separately so the
  // size rule ignores it and the schedule can name it.
  modelingFee = 0,
  // The final-payment terms this quote was sold under — see finalCopy().
  // Legacy by default, for the same reason as milestonePolicy.
  finalPaymentPolicy = FINAL_POLICY_LEGACY,
}) {
  const setup = isAlacarte ? 0 : num(setupFee); // à la carte has no platform fee
  const viz = num(vizSubtotal);
  const travel = num(travelCost);
  const collateral = num(collateralTotal);
  const modeling = Math.min(Math.max(0, num(modelingFee)), viz);
  const oneTimeScope = oneTimeScopeOf({ setupFee: setup, vizSubtotal: viz, travelCost: travel, collateralTotal: collateral });
  // The $100k size rule ignores the 3D modeling fee: a fee the client may get
  // back must not be what moves a quote onto the 25 / 50 / 25 plan.
  const sizeScope = oneTimeScope - modeling;
  const planKey = resolvePlanKey({ oneTimeScope: sizeScope, isAlacarte, alacartePayPlan, payPlanChoice });
  const singlePayment = isSinglePayment(planKey);
  const pctArr = PLAN_PCT[planKey] || PLAN_PCT[PLAN_50_25_25];
  const { p1, p2, p3 } = splitPayments({ setupFee: setup, vizSubtotal: viz, travelCost: travel, collateralTotal: collateral, planKey });
  const setupInSplit = SETUP_IN_SPLIT_PLANS.includes(planKey) && setup > 0;
  const collateralInSplit = collateralInSplitOf({ setupFee: setup, vizSubtotal: viz, collateralTotal: collateral });
  const hasViz = viz - modeling > 0;
  const hasModeling = modeling > 0;
  const parts = { setupInSplit, hasViz, collateralInSplit, hasModeling };
  const v1 = resolveV1Milestone(scope || {}, { isAlacarte, milestonePolicy });
  // The plan's own one-time money, split into what earns the pay-in-full
  // discount and what is billed at cost. Surfaces carrying one-time money the
  // plan does not divide (the marketing campaign setup, a marketing-and-
  // collateral quote's standalone collateral) fold it into `pifDiscountable`
  // and call pifOffer() again — see ProposalPage / the PDF investment pages.
  const pifDiscountable = p1 + p2 + p3 - travel;
  const pifPct = pifPctOf(pifDiscount);
  // On a single-payment plan the schedule IS the pay-in-full, so the discount
  // is priced INTO Payment 1 rather than offered underneath it. Printing both
  // would put two prices for the same behaviour on one page — the milestone at
  // full price and a box beneath it offering the identical payment for 5%
  // less. The rep's existing Discount control still decides whether there is
  // one: off, this is simply the whole scope in one payment.
  const single = singlePayment
    ? pifOffer({ discountable: pifDiscountable, atCost: travel, pct: pifPct })
    : null;
  const payments = single
    ? { p1: single.total, p2: 0, p3: 0 }
    : { p1, p2, p3 };
  return {
    planKey,
    payPlan: planKey,
    frontLoaded: planKey === PLAN_50_25_25,
    // Two payments: signing, then finalization (the à-la-carte 50/50 and the
    // 75/25). Payment 2 IS the final payment; Payment 3 is zero.
    twoPart: isTwoPart(planKey),
    // One payment at signing, chosen by the rep. Surfaces render ONE row and
    // suppress the pay-in-full block — see `pif.eligible` below.
    singlePayment,
    // What the client saves by the terms being a single payment, so the row can
    // say so. Zero when the rep switched the discount off.
    singlePaymentSavings: single ? single.savings : 0,
    singlePaymentGross: single ? pifDiscountable + travel : 0,
    largeQuote: isLargeQuote(sizeScope),
    isAlacarte: !!isAlacarte,
    oneTimeScope,
    pct: { p1: pctArr[0], p2: pctArr[1], p3: pctArr[2] },
    // What the percentages are a percentage OF, for client-facing copy.
    // Three registers: client view, internal sidebar, formal proposal.
    ...scopeLabels(parts),
    // Same scope, delineated per component so every milestone states its own
    // visualization percentage. Join with " · ". Mirrors `scope_parts()` in
    // services/vexos_payment_plan.py — keep the two in step.
    scopeParts: {
      p1: scopeParts(pctArr[0], parts),
      p2: scopeParts(pctArr[1], parts),
      p3: scopeParts(pctArr[2], { ...parts, remaining: true }),
    },
    setupInSplit,
    // Collateral rides the plan percentages above. When FALSE and the quote
    // still carries collateral (marketing & collateral only), the surface owns
    // the standalone 50 / 50 signing / delivery schedule instead.
    collateralInSplit,
    // There IS a setup fee and it bills whole at signing (large-quote plan).
    setupAtSigning: !setupInSplit && setup > 0,
    ...payments,
    v1,
    // Payment 3 copy names no asset TYPE (see FINAL_ASSETS) but does have to
    // name collateral when collateral is part of the amount.
    finalAssets: finalCopy(parts).assets,
    finalNote: finalCopy({ ...parts, policy: finalPaymentPolicy }).note,
    finalPaymentPolicy,
    // Pay in full: the alternative to the milestones above. Travel rides it at
    // cost. `pifDiscountable` / `pifAtCost` are exposed so a surface that also
    // bills marketing setup or standalone collateral can rebuild the offer
    // over the whole one-time total instead of only the plan's slice.
    pifDiscountable,
    pifAtCost: travel,
    // The discount rate this quote's offer carries — 10, or 0 when the rep
    // switched the discount off. A surface that rebuilds the offer over a
    // wider pot of one-time money MUST pass this back into pifOffer() and
    // pifCopy(), or it will re-apply a discount the rep turned off.
    pifPct,
    // The alternative to the milestones. Never offered against a schedule that
    // is already one payment — there would be nothing to switch to.
    pif: singlePayment
      ? { ...single, eligible: false }
      : pifOffer({ discountable: pifDiscountable, atCost: travel, pct: pifPct }),
  };
}

/** Internal one-liner for the rep: which side of the size rule this quote fell
 *  on, and what that did to the setup fee. Null on the 50/50 plan, which the
 *  rep picked deliberately and does not need explaining. */
export function planNote(plan) {
  // The calculator's schedule carries the key as `payPlan`, the resolver as
  // `planKey` — read either.
  if (plan && (plan.planKey || plan.payPlan) === PLAN_75_25) {
    return "75 / 25 — 75% at signing, the remaining 25% on finalization, collected before final delivery. No V1 payment in between.";
  }
  if (!plan || plan.twoPart) return null;
  if (plan.singlePayment) {
    return plan.singlePaymentSavings > 0
      ? `Single payment — the whole one-time scope at signing, with the ${plan.pifPct}% pay-in-full discount applied. The separate pay-in-full option is not printed.`
      : "Single payment — the whole one-time scope at signing, at full price. Switch the discount on to price the pay-in-full saving into it.";
  }
  const threshold = `$${LARGE_QUOTE_THRESHOLD.toLocaleString()}`;
  if (plan.largeQuote) {
    return `Over ${threshold} one-time — this quote qualifies for 25 / 50 / 25${plan.setupAtSigning ? ", with the VEXOS setup fee billed in full at signing" : ""}.`;
  }
  return `${threshold} or under one-time — this quote collects 50 / 25 / 25${plan.setupInSplit ? ", with the VEXOS setup fee split across all three payments" : ""}.`;
}
